import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { GraduationCap, Building, Award, Heart } from 'lucide-react';

export default function About() {
  const { t } = useTranslation();

  const qualifications = [
    { icon: GraduationCap, text: 'MBBS (Assam Medical College, Dibrugarh)' },
    { icon: GraduationCap, text: 'DLO (Silchar Medical College)' },
    { icon: Building, text: 'Retired SDMO, Biswanath' },
    { icon: Award, text: 'President, Indian Medical Association, Biswanath Branch' }
  ];

  return (
    <div className="min-h-screen py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6" data-testid="about-page-title">
            {t('about.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto" data-testid="about-page-subtitle">
            {t('about.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-full h-96 bg-primary/10 rounded-3xl shadow-2xl flex items-center justify-center" data-testid="doctor-profile-placeholder">
              <div className="text-center p-8">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-5xl font-bold text-white">Dr</span>
                </div>
                <h3 className="text-2xl font-bold text-primary mb-2">Dr. N.C. Upadhyaya</h3>
                <p className="text-sm text-muted-foreground">MBBS (Assam Medical College, Dibrugarh)</p>
                <p className="text-sm text-muted-foreground">DLO (Silchar Medical College)</p>
                <p className="text-sm text-muted-foreground mt-3 font-semibold">Chief ENT Consultant</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">Dr. N.C. Upadhyaya</h2>
              <p className="text-muted-foreground leading-relaxed" data-testid="doctor-description">
                {t('about.description')}
              </p>
            </div>

            <div className="space-y-4">
              {qualifications.map((qual, index) => (
                <div key={index} className="flex items-start gap-4" data-testid={`qualification-${index}`}>
                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                    <qual.icon size={24} className="text-primary" />
                  </div>
                  <div className="pt-2">
                    <p className="font-medium text-foreground">{qual.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              title: 'Experience',
              value: '30+',
              subtitle: 'Years of Practice',
              description: 'Three decades of dedicated service to ENT care'
            },
            {
              title: 'Community',
              value: 'Trusted',
              subtitle: 'Local Healthcare Provider',
              description: 'Serving Biswanath and surrounding areas with excellence'
            },
            {
              title: 'Expertise',
              value: 'Advanced',
              subtitle: 'Medical Equipment',
              description: 'Modern facilities for accurate diagnosis and treatment'
            }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 text-center card-hover" data-testid={`stat-card-${index}`}>
                <p className="text-4xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm font-semibold text-foreground mb-2">{stat.subtitle}</p>
                <p className="text-xs text-muted-foreground">{stat.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
